import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useAdminAPI } from '../hooks/useAdminAPI';
import { CulturalEvent } from '../constants/culturalEventsTypes';
import './AdminUpload.css';

// Move validateFile outside component to avoid dependency issues
const validateFile = (file: File, setError: (error: string) => void): boolean => {
  // Check file type
  if (file.type !== 'application/pdf') {
    setError('Please upload a PDF file');
    return false;
  }
  
  // Check file size (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    setError('File size must be less than 10MB');
    return false;
  }
  
  setError('');
  return true;
};

export const AdminUpload: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAdminAuth();
  const { uploadPDF, updateEvent, publishEvent, loading } = useAdminAPI();
  
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState<string>('');
  const [extractedEvent, setExtractedEvent] = useState<CulturalEvent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedEvent, setEditedEvent] = useState<Partial<CulturalEvent>>({});
  const [error, setError] = useState<string>('');
  const uploadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
      }
    };
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file, setError)) {
        setSelectedFile(file);
      }
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file, setError)) {
        setSelectedFile(file);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setError('');
      setProcessingStage('Uploading file...');
      setUploadProgress(10);
      
      // Clear any existing timeout
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
      }
      
      // Set timeout for long operations (5 minutes)
      uploadTimeoutRef.current = setTimeout(() => {
        setError('Upload timeout: The AI extraction is taking longer than expected. Please try again.');
        setUploadProgress(0);
        setProcessingStage('');
      }, 5 * 60 * 1000); // 5 minutes
      
      console.log('📤 Starting upload...');
      
      // Simulate progress updates during processing
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev < 90) {
            setProcessingStage(prev < 30 ? 'Uploading file...' : prev < 60 ? 'Extracting text from PDF...' : 'Processing with AI...');
            return prev + 5;
          }
          return prev;
        });
      }, 2000);
      
      const response = await uploadPDF(selectedFile);
      
      // Clear progress interval and timeout
      clearInterval(progressInterval);
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
        uploadTimeoutRef.current = null;
      }
      
      setUploadProgress(100);
      setProcessingStage('Complete!');
      console.log('✅ Upload complete:', response);
      
      // Handle response structure - backend now returns _id
      const eventData = response.event || response;
      const event = {
        ...eventData,
        _id: eventData._id || eventData.id // Support both for backward compatibility
      };
      console.log('📋 Processed event data:', event);
      setExtractedEvent(event);
      setEditedEvent(event); // Initialize edited event with extracted data
      setSelectedFile(null);
      setProcessingStage('');
    } catch (err: any) {
      // Clear timeout on error
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
        uploadTimeoutRef.current = null;
      }
      
      // Improved error message handling
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          err.message || 
                          'Upload failed. Please try again.';
      
      const errorDetails = err.response?.data?.details;
      const fullError = errorDetails 
        ? `${errorMessage}\n\nDetails: ${Array.isArray(errorDetails) ? errorDetails.map((d: any) => `\n- ${d.field || ''}: ${d.message || d}`).join('') : errorDetails}`
        : errorMessage;
      
      setError(fullError);
      setUploadProgress(0);
      setProcessingStage('');
      console.error('❌ Upload error:', err);
    }
  };

  const handleSave = async () => {
    if (!extractedEvent || !extractedEvent._id) {
      console.error('❌ Cannot save: No event ID', extractedEvent);
      setError('Cannot save: Event ID is missing');
      return;
    }
    
    try {
      setError('');
      
      // Prepare update data - merge with original event to ensure all required fields are present
      const updateData: Partial<CulturalEvent> = {
        ...extractedEvent,
        ...editedEvent
      };
      
      // Ensure date is in ISO format (YYYY-MM-DD)
      if (updateData.date) {
        const dateStr = updateData.date.toString();
        // If it's just a date string (YYYY-MM-DD), convert to ISO date
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          updateData.date = new Date(dateStr + 'T00:00:00').toISOString().split('T')[0];
        } else if (dateStr.includes('T')) {
          updateData.date = dateStr.split('T')[0];
        }
      }
      
      // Ensure time is in HH:mm format
      if (updateData.time && updateData.time.length > 5) {
        updateData.time = updateData.time.substring(0, 5);
      }
      
      // Convert price to number
      if (updateData.price !== undefined) {
        updateData.price = typeof updateData.price === 'string' 
          ? parseFloat(updateData.price) || 0 
          : updateData.price;
      }
      
      // Handle maxAttendees - convert empty string/null to undefined (optional field)
      // Handle both number and string types (from form inputs)
      const maxAttendeesValue = updateData.maxAttendees as number | string | undefined | null;
      if (maxAttendeesValue === '' || maxAttendeesValue === null || maxAttendeesValue === undefined) {
        updateData.maxAttendees = undefined;
      } else {
        const max = typeof maxAttendeesValue === 'string' 
          ? parseInt(maxAttendeesValue) 
          : maxAttendeesValue;
        // Only include if it's a valid positive number
        updateData.maxAttendees = (!isNaN(max) && max >= 1) ? max : undefined;
      }
      
      // Remove _id and undefined fields from update data (backend doesn't need them)
      const { _id, ...dataToSend } = updateData;
      
      // Remove undefined fields to avoid sending them
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === undefined) {
          delete dataToSend[key];
        }
      });
      
      console.log('💾 Saving event:', extractedEvent._id);
      console.log('📦 Data being sent:', dataToSend);
      
      const updated = await updateEvent(extractedEvent._id, dataToSend);
      console.log('✅ Event saved:', updated);
      
      // Update the extracted event with edited data
      setExtractedEvent({ ...extractedEvent, ...editedEvent });
      setIsEditing(false);
      alert('Event saved successfully!');
    } catch (error: any) {
      console.error('❌ Save error:', error);
      const errorDetails = error.response?.data?.details || [];
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save event';
      const fullError = errorDetails.length > 0 
        ? `${errorMessage}\n\nDetails:\n${errorDetails.map((e: any) => `- ${e.field}: ${e.message}`).join('\n')}`
        : errorMessage;
      setError(fullError);
      alert(`Failed to save:\n${fullError}`);
    }
  };

  const handlePublish = async () => {
    if (!extractedEvent || !extractedEvent._id) {
      console.error('❌ Cannot publish: No event ID', extractedEvent);
      setError('Cannot publish: Event ID is missing');
      return;
    }
    
    // If editing, save first
    if (isEditing) {
      try {
        // Prepare update data - merge with original event
        const updateData: Partial<CulturalEvent> = {
          ...extractedEvent,
          ...editedEvent
        };
        
        // Ensure status is draft (to allow past dates)
        if (!updateData.status) {
          updateData.status = 'draft';
        }
        
        // Ensure date is in ISO format (YYYY-MM-DD) for ISO8601 validation
        if (updateData.date) {
          const dateStr = updateData.date.toString();
          if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            updateData.date = dateStr; // Already in correct format
          } else if (dateStr.includes('T')) {
            updateData.date = dateStr.split('T')[0]; // Extract date part
          } else {
            // Try to parse and format
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              updateData.date = `${year}-${month}-${day}`;
            }
          }
        }
        
        // Ensure time is in HH:mm format
        if (updateData.time && updateData.time.length > 5) {
          updateData.time = updateData.time.substring(0, 5);
        }
        
        // Convert price to number
        if (updateData.price !== undefined) {
          updateData.price = typeof updateData.price === 'string' 
            ? parseFloat(updateData.price) || 0 
            : updateData.price;
        }
        
        // Handle maxAttendees - convert empty string/null to undefined (optional field)
        // Handle both number and string types (from form inputs)
        const maxAttendeesValue = updateData.maxAttendees as number | string | undefined | null;
        if (maxAttendeesValue === '' || maxAttendeesValue === null || maxAttendeesValue === undefined) {
          updateData.maxAttendees = undefined;
        } else {
          const max = typeof maxAttendeesValue === 'string' 
            ? parseInt(maxAttendeesValue) 
            : maxAttendeesValue;
          // Only include if it's a valid positive number
          updateData.maxAttendees = (!isNaN(max) && max >= 1) ? max : undefined;
        }
        
        // Remove _id and undefined fields from update data
        const { _id, ...dataToSend } = updateData;
        
        // Remove undefined fields to avoid sending them
        Object.keys(dataToSend).forEach(key => {
          if (dataToSend[key] === undefined) {
            delete dataToSend[key];
          }
        });
        
        console.log('💾 Saving before publish:', extractedEvent._id);
        console.log('📦 Data being sent:', dataToSend);
        await updateEvent(extractedEvent._id, dataToSend);
        setExtractedEvent({ ...extractedEvent, ...editedEvent });
        setIsEditing(false);
      } catch (error: any) {
        console.error('❌ Save before publish error:', error);
        const errorDetails = error.response?.data?.details || [];
        const errorMessage = error.response?.data?.message || error.message || 'Failed to save event';
        const fullError = errorDetails.length > 0 
          ? `${errorMessage}\n\nDetails:\n${errorDetails.map((e: any) => `- ${e.field}: ${e.message}`).join('\n')}`
          : errorMessage;
        setError(fullError);
        alert(`Failed to save: ${fullError}`);
        return;
      }
    }
    
    if (window.confirm('Publish this event now?')) {
      try {
        console.log('🚀 Publishing event:', extractedEvent._id);
        await publishEvent(extractedEvent._id);
        alert('Event published successfully!');
        navigate('/admin/cultural-events/dashboard');
      } catch (error: any) {
        console.error('❌ Publish error:', error);
        const errorDetails = error.response?.data?.details || [];
        const errorMessage = error.response?.data?.message || error.message || 'Failed to publish event';
        const fullError = errorDetails.length > 0 
          ? `${errorMessage}\n\nDetails:\n${errorDetails.map((e: any) => `- ${e.field}: ${e.message}`).join('\n')}`
          : errorMessage;
        setError(fullError);
        alert(`Failed to publish: ${fullError}`);
      }
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedEvent(extractedEvent || {});
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedEvent(extractedEvent || {});
    setError('');
  };

  const handleFieldChange = (field: keyof CulturalEvent, value: any) => {
    setEditedEvent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDiscard = () => {
    if (window.confirm('Discard this extracted event?')) {
      setExtractedEvent(null);
      setSelectedFile(null);
      setUploadProgress(0);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="admin-upload">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-container">
          <div className="header-content">
            <h1>Upload Event PDF</h1>
            <div className="header-actions">
              <button 
                className="btn-dashboard"
                onClick={() => navigate('/admin/cultural-events/dashboard')}
              >
                ← Dashboard
              </button>
              <button className="btn-logout" onClick={logout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="upload-content">
        <div className="admin-container">
          {!extractedEvent ? (
            <>
              {/* Upload Section */}
              <div className="upload-section">
                <div 
                  className={`dropzone ${dragActive ? 'active' : ''} ${selectedFile ? 'has-file' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {!selectedFile ? (
                    <>
                      <div className="dropzone-icon">📄</div>
                      <h3>Drag & Drop PDF Here</h3>
                      <p>or</p>
                      <label htmlFor="file-input" className="file-input-label">
                        Browse Files
                      </label>
                      <input
                        id="file-input"
                        type="file"
                        accept=".pdf"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      <p className="dropzone-hint">
                        Maximum file size: 10MB
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="dropzone-icon">✅</div>
                      <h3>{selectedFile.name}</h3>
                      <p>{(selectedFile.size / 1024).toFixed(2)} KB</p>
                      <div className="file-actions">
                        <button 
                          className="btn-upload-action"
                          onClick={handleUpload}
                          disabled={loading}
                        >
                          {loading ? 'Uploading...' : 'Upload & Extract'}
                        </button>
                        <button 
                          className="btn-cancel"
                          onClick={() => setSelectedFile(null)}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {error && (
                  <div className="upload-error">
                    ❌ {error}
                  </div>
                )}

                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p>
                      {processingStage || 'Processing...'} {uploadProgress}%
                      {uploadProgress > 60 && (
                        <span style={{ display: 'block', fontSize: '0.9em', color: '#666', marginTop: '4px' }}>
                          AI extraction may take 30-60 seconds...
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="upload-instructions">
                <h3>📋 How It Works</h3>
                <ol>
                  <li>Upload a PDF file containing event information in Russian</li>
                  <li>Claude AI will automatically extract:
                    <ul>
                      <li>Event title and description</li>
                      <li>Date and time</li>
                      <li>Location and venue</li>
                      <li>Price and payment link</li>
                      <li>Event type</li>
                    </ul>
                  </li>
                  <li>Review the extracted data</li>
                  <li>Publish or save as draft</li>
                </ol>
              </div>
            </>
          ) : (
            /* Extraction Result */
            <div className="extraction-result">
              <div className="result-header">
                <h2>✅ Event Extracted Successfully!</h2>
                <p>{isEditing ? 'Edit the fields below and save or publish when ready' : 'Review the information below and edit or publish as needed'}</p>
              </div>

              {error && (
                <div className="upload-error">
                  ❌ {error}
                </div>
              )}

              <div className="result-card">
                {!isEditing ? (
                  <>
                    <div className="result-section">
                      <h3>Basic Information</h3>
                      <div className="result-grid">
                        <div className="result-item">
                          <label>Title:</label>
                          <span>{extractedEvent.title}</span>
                        </div>
                        <div className="result-item">
                          <label>Date:</label>
                          <span>{formatDate(extractedEvent.date)}</span>
                        </div>
                        <div className="result-item">
                          <label>Time:</label>
                          <span>{extractedEvent.time}</span>
                        </div>
                        <div className="result-item">
                          <label>Event Type:</label>
                          <span style={{ textTransform: 'capitalize' }}>
                            {extractedEvent.eventType}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="result-section">
                      <h3>Description</h3>
                      <p>{extractedEvent.description}</p>
                    </div>

                    <div className="result-section">
                      <h3>Location</h3>
                      <div className="result-grid">
                        <div className="result-item">
                          <label>Venue:</label>
                          <span>{extractedEvent.location}</span>
                        </div>
                        {extractedEvent.address && (
                          <div className="result-item">
                            <label>Address:</label>
                            <span>{extractedEvent.address}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="result-section">
                      <h3>Pricing</h3>
                      <div className="result-grid">
                        <div className="result-item">
                          <label>Price:</label>
                          <span>
                            {extractedEvent.isFree 
                              ? 'Free' 
                              : `£${extractedEvent.price}`
                            }
                          </span>
                        </div>
                        {extractedEvent.paymentLink && (
                          <div className="result-item">
                            <label>Payment Link:</label>
                            <a 
                              href={extractedEvent.paymentLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              View Stripe Link
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="result-actions">
                      <button 
                        className="btn-edit"
                        onClick={handleEdit}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="btn-discard"
                        onClick={handleDiscard}
                      >
                        🗑️ Discard
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="result-section">
                      <h3>Basic Information</h3>
                      <div className="result-grid">
                        <div className="result-item">
                          <label>Title:</label>
                          <input
                            type="text"
                            value={editedEvent.title || ''}
                            onChange={(e) => handleFieldChange('title', e.target.value)}
                            className="edit-input"
                          />
                        </div>
                        <div className="result-item">
                          <label>Date:</label>
                          <input
                            type="date"
                            value={editedEvent.date ? (editedEvent.date.includes('T') ? editedEvent.date.split('T')[0] : editedEvent.date.split(' ')[0]) : ''}
                            onChange={(e) => handleFieldChange('date', e.target.value)}
                            className="edit-input"
                          />
                        </div>
                        <div className="result-item">
                          <label>Time:</label>
                          <input
                            type="time"
                            value={editedEvent.time || ''}
                            onChange={(e) => handleFieldChange('time', e.target.value)}
                            className="edit-input"
                          />
                        </div>
                        <div className="result-item">
                          <label>Event Type:</label>
                          <select
                            value={editedEvent.eventType || 'other'}
                            onChange={(e) => handleFieldChange('eventType', e.target.value)}
                            className="edit-input"
                          >
                            <option value="concert">Concert</option>
                            <option value="exhibition">Exhibition</option>
                            <option value="theater">Theater</option>
                            <option value="workshop">Workshop</option>
                            <option value="festival">Festival</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="result-section">
                      <h3>Description</h3>
                      <textarea
                        value={editedEvent.description || ''}
                        onChange={(e) => handleFieldChange('description', e.target.value)}
                        className="edit-textarea"
                        rows={4}
                      />
                      {editedEvent.detailedDescription !== undefined && (
                        <>
                          <label style={{ display: 'block', marginTop: '16px', marginBottom: '8px' }}>
                            Detailed Description (optional):
                          </label>
                          <textarea
                            value={editedEvent.detailedDescription || ''}
                            onChange={(e) => handleFieldChange('detailedDescription', e.target.value)}
                            className="edit-textarea"
                            rows={3}
                          />
                        </>
                      )}
                    </div>

                    <div className="result-section">
                      <h3>Location</h3>
                      <div className="result-grid">
                        <div className="result-item">
                          <label>Venue:</label>
                          <input
                            type="text"
                            value={editedEvent.location || ''}
                            onChange={(e) => handleFieldChange('location', e.target.value)}
                            className="edit-input"
                          />
                        </div>
                        <div className="result-item">
                          <label>Address (optional):</label>
                          <input
                            type="text"
                            value={editedEvent.address || ''}
                            onChange={(e) => handleFieldChange('address', e.target.value)}
                            className="edit-input"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="result-section">
                      <h3>Pricing</h3>
                      <div className="result-grid">
                        <div className="result-item">
                          <label>
                            <input
                              type="checkbox"
                              checked={editedEvent.isFree || false}
                              onChange={(e) => {
                                handleFieldChange('isFree', e.target.checked);
                                if (e.target.checked) {
                                  handleFieldChange('price', 0);
                                }
                              }}
                            />
                            {' '}Free Event
                          </label>
                          {!editedEvent.isFree && (
                            <input
                              type="number"
                              value={editedEvent.price || 0}
                              onChange={(e) => handleFieldChange('price', parseFloat(e.target.value) || 0)}
                              className="edit-input"
                              min="0"
                              step="0.01"
                              placeholder="Price"
                            />
                          )}
                        </div>
                        <div className="result-item">
                          <label>Payment Link (optional):</label>
                          <input
                            type="url"
                            value={editedEvent.paymentLink || ''}
                            onChange={(e) => handleFieldChange('paymentLink', e.target.value)}
                            className="edit-input"
                            placeholder="https://buy.stripe.com/..."
                          />
                        </div>
                        <div className="result-item">
                          <label>Max Attendees (optional):</label>
                          <input
                            type="number"
                            value={editedEvent.maxAttendees || ''}
                            onChange={(e) => handleFieldChange('maxAttendees', e.target.value ? parseInt(e.target.value) : undefined)}
                            className="edit-input"
                            min="1"
                            placeholder="Leave empty for unlimited"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="result-actions">
                      <button 
                        className="btn-save"
                        onClick={handleSave}
                        disabled={loading}
                      >
                        💾 Save
                      </button>
                      <button 
                        className="btn-publish-now"
                        onClick={handlePublish}
                        disabled={loading}
                      >
                        🚀 Publish
                      </button>
                      <button 
                        className="btn-cancel"
                        onClick={handleCancelEdit}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>

              <button 
                className="btn-upload-another"
                onClick={() => {
                  setExtractedEvent(null);
                  setSelectedFile(null);
                  setUploadProgress(0);
                }}
              >
                + Upload Another Event
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

